import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Camera } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/7810981/pexels-photo-7810981.jpeg";

const LINES = ["We Create", "Community", "Experiences."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ onSchedule }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden">
      {/* decorative blobs */}
      <div className="pointer-events-none absolute -top-10 -left-16 h-72 w-72 rounded-full bg-sky/40 blur-3xl" />
      <div className="pointer-events-none absolute top-40 right-1/3 h-56 w-56 rounded-full bg-marigold/30 blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-white/70 border border-border px-4 py-2 mb-7"
          >
            <span className="h-2 w-2 rounded-full bg-coral animate-pulse" />
            <span className="text-sm font-semibold text-charcoal/80">For senior communities & their families</span>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-poppins font-extrabold text-charcoal text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.02] tracking-tighter">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {i === 1 ? <span className="text-coral">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-local-line"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-5 max-w-xl text-base md:text-lg font-semibold text-charcoal/70"
          >
            Senior living events and resident portrait experiences serving South Bay and Los Angeles County.
          </motion.p>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-7 max-w-xl text-lg md:text-xl leading-relaxed text-charcoal/75"
          >
            Meaningful experiences that bring residents and families together.
            Professional photography preserves the memory.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-schedule-btn"
              onClick={onSchedule}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-coral text-cream font-semibold text-lg px-8 py-4 transition-all hover:-translate-y-1 hover:shadow-[0_24px_44px_-16px_rgba(240,125,106,0.65)]"
            >
              Request a 15-Minute Conversation
              <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2.5rem] overflow-hidden shadow-[0_40px_80px_-30px_rgba(73,53,72,0.35)] aspect-[4/5]"
          >
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMG}
              alt="Residents and families attending a senior living community celebration"
              className="absolute inset-0 h-[115%] w-full object-cover"
            />
          </motion.div>

          <motion.div
            style={{ y: badgeY }}
            className="absolute -bottom-5 -left-5 md:-left-10 bg-white rounded-3xl p-5 shadow-[0_24px_50px_-20px_rgba(43,48,51,0.25)] max-w-[230px]"
          >
            <div className="flex items-center gap-2 text-coral mb-1.5">
              <Camera size={18} />
              <span className="text-xs font-bold uppercase tracking-wider">The Experience</span>
            </div>
            <p className="text-sm font-semibold text-charcoal leading-snug">
              A warm 90-minute gathering that brings residents and families together.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
